import type { DestinationRecordFragment } from '~/libs/dato/fragments/destinationFragment';
import type { ResponsiveImage } from '~/libs/dato/fragments/responsiveImageFragment';
import type { MenuItem } from '~/types/Menu.types';
import { getDestinationUrl } from '~/utils/datoHelpers';

/*
 * Transform for the per-locale nav + footer models (G2-4009). Emits a clean recursive `MenuItem[]`:
 * `navigation_group` → structural node (title + nested items, no url); `navigation_item` → leaf link.
 * The client renders by structure (group vs leaf), not by hardcoded index. Input is hand-typed rather
 * than the gql.tada result type because the query expands the recursive `navigation_group` to a fixed
 * depth. Each nesting level has a structurally different generated type — a single recursive
 * function can only be typed against a permissive shape (same reason the legacy transforms use `any`).
 */

interface RawRecordLink {
  __typename: string;
  slug?: string | null;
  destination?: DestinationRecordFragment['destination'];
}

interface RawNavigationLink {
  _modelApiKey: string;
  internalLink?: string | null;
  externalLink?: string | null;
  recordLink?: RawRecordLink | null;
}

interface RawNavigationTile {
  id: string;
  _modelApiKey: string;
  title?: string | null;
  excerpt?: string | null;
  image?: { responsiveImage?: ResponsiveImage | null } | null;
  link?: RawNavigationLink | null;
}

export interface RawNavigationNode {
  id: string;
  _modelApiKey: string;
  __typename?: string;
  // navigation_item (leaf)
  label?: string | null;
  link?: RawNavigationLink | null;
  // navigation_group / dynamic_blogposts_section
  title?: string | null;
  items?: RawNavigationNode[] | null;
  tiledLinks?: RawNavigationTile[] | null;
}

export interface NavigationBlogpost {
  id: string;
  title: string;
  slug: string;
}

// The Journal block has no CMS-authored links; they're filled from the latest blogposts.
export const isDynamicBlogpostsMarker = (item: { __typename?: string }): boolean =>
  item.__typename === 'DynamicBlogpostsSectionRecord';

const FALLBACK_URL = '#';

const resolveRecordUrl = (record: RawRecordLink): string => {
  const slug = record.slug ?? '';
  switch (record.__typename) {
    case 'DestinationRecord':
      return getDestinationUrl({ slug, destination: record.destination });
    case 'AboutRecord':
      return `/about/${slug}`;
    case 'BlogpostRecord':
      return `/journal/${slug}`;
    case 'InspirationRecord':
      return `/inspiration/${slug}`;
    case 'LegalPageRecord':
      return `/legal/${slug}`;
    // sitePage is a root-level catch-all slug;
    case 'SitePageRecord':
      return `/${slug}`;
    default:
      return FALLBACK_URL;
  }
};

const resolveLink = (link: RawNavigationLink | null | undefined): { url: string; isExternal: boolean } => {
  if (link?.internalLink != null) {
    return { url: link.internalLink, isExternal: false };
  }
  if (link?.externalLink != null) {
    return { url: link.externalLink, isExternal: true };
  }
  if (link?.recordLink) {
    return { url: resolveRecordUrl(link.recordLink), isExternal: false };
  }
  return { url: FALLBACK_URL, isExternal: false };
};

// A group carries nested `items`; a leaf carries a `link`.
const isGroup = (node: RawNavigationNode): boolean => Array.isArray(node.items);

// navigation_group `tiledLinks` → image tiles for the dropdown's tile pane. Each carries its own
// image + title + excerpt and links via the same three kinds as a leaf, so `resolveLink` is reused.
const transformTiles = (tiles: RawNavigationTile[] | null | undefined): MenuItem[] =>
  (tiles ?? [])
    .filter((tile): tile is RawNavigationTile => tile != null)
    .map((tile) => {
      const { url, isExternal } = resolveLink(tile.link);
      return {
        id: tile.id,
        title: tile.title?.trim() ?? '',
        excerpt: tile.excerpt?.trim() ?? undefined,
        url,
        isExternal,
        _modelApiKey: tile._modelApiKey,
        backgroundImage: tile.image ?? undefined
      };
    });

export const transformNavigationItems = (
  items: RawNavigationNode[] | null | undefined,
  blogposts: NavigationBlogpost[] = []
): MenuItem[] => {
  if (!items) {
    return [];
  }

  return items
    .filter((item): item is RawNavigationNode => item != null)
    .map((item) => {
      // Every marker gets the same posts — there may be more than one, and they are fetched once.
      if (isDynamicBlogpostsMarker(item)) {
        return {
          id: item.id,
          title: item.title?.trim() ?? '',
          __typename: item.__typename,
          items: blogposts.map((post) => ({
            id: post.id,
            title: post.title,
            url: `/journal/${post.slug}`,
            isExternal: false
          }))
        };
      }

      if (isGroup(item)) {
        const tiles = transformTiles(item.tiledLinks);
        return {
          id: item.id,
          title: item.title?.trim() ?? '',
          _modelApiKey: item._modelApiKey,
          items: transformNavigationItems(item.items, blogposts),
          // Omit `tiles` entirely when a group has none, so tile-less groups stay shape-clean.
          ...(tiles.length > 0 ? { tiles } : {})
        };
      }

      const { url, isExternal } = resolveLink(item.link);
      return {
        id: item.id,
        title: item.label?.trim() ?? '',
        url,
        isExternal,
        _modelApiKey: item._modelApiKey
      };
    });
};
